/**
 * Retention Bulk Runner Mixin
 *
 * Generic queue + concurrency-controlled fan-out for bulk operations on
 * retention defaults, messages and images. Owns the progress + result state;
 * the consumer supplies a `worker(item)` async function.
 *
 * Failure shape is `{ item, message, appleErrorCode, status }` so consumers can
 * render either a locale code, a productId, or a message/image id without the
 * mixin caring which, and so retryability can be decided from the HTTP status.
 */

const DEFAULT_CONCURRENCY = 5;
export const APPLE_INVALID_PRODUCT_CODE = 4000023;

/**
 * Decide whether a failed bulk item is worth retrying. Status-based: the whole
 * chain (config-manager → retention-messaging-service → Apple) forwards the real
 * HTTP status verbatim, so network/timeout (no response), 429 and any 5xx are
 * transient; every 4xx (validation, content rejection, blocking references,
 * wrong-state, auth, not-found) and Apple's invalid-product code are terminal.
 */
export function isRetryableFailure(f) {
    if (f.appleErrorCode === APPLE_INVALID_PRODUCT_CODE) return false;
    if (f.status == null) return true;
    return f.status === 429 || f.status >= 500;
}

export const retentionBulkRunnerMixin = {
    data() {
        return {
            bulkRunning: false,
            bulkProgress: { total: 0, done: 0, successes: [], failures: [] },
            bulkResult: { successes: [], failures: [] },
            showResultModal: false
        };
    },
    computed: {
        // Failures Apple confirmed are non-retryable: invalid product ID. Drives the
        // defaults pages' bespoke "rejected by Apple" note.
        invalidProductFailures() {
            return this.bulkResult.failures.filter(
                (f) => f.appleErrorCode === APPLE_INVALID_PRODUCT_CODE
            );
        },
        // Transient failures (network, timeout, 429, 5xx) — re-runnable via retryFailed.
        retryableFailures() {
            return this.bulkResult.failures.filter(isRetryableFailure);
        },
        // Terminal failures (4xx content/state/auth/not-found, invalid product) — not retried.
        terminalFailures() {
            return this.bulkResult.failures.filter((f) => !isRetryableFailure(f));
        }
    },
    methods: {
        async executeBulk({ items, worker, concurrency = DEFAULT_CONCURRENCY }) {
            this.bulkRunning = true;
            this.bulkProgress = { total: items.length, done: 0, successes: [], failures: [] };
            const queue = [...items];

            const workerFn = async () => {
                while (queue.length) {
                    const item = queue.shift();
                    try {
                        await worker(item);
                        this.bulkProgress.successes.push(item);
                    } catch (err) {
                        const apple = err?.response?.data?.apple;
                        const appleText = apple?.errorMessage
                            ? `${apple.errorMessage}${apple.errorCode ? ` [${apple.errorCode}]` : ''}`
                            : null;
                        this.bulkProgress.failures.push({
                            item,
                            message:
                                appleText ||
                                err?.response?.data?.message ||
                                err?.message ||
                                'Unknown error',
                            appleErrorCode: apple?.errorCode || null,
                            status: err?.response?.status ?? null
                        });
                    } finally {
                        this.bulkProgress.done++;
                    }
                }
            };

            const workerCount = Math.min(concurrency, items.length);
            await Promise.all(Array.from({ length: workerCount }, () => workerFn()));

            this.bulkResult = {
                successes: [...this.bulkProgress.successes],
                failures: [...this.bulkProgress.failures]
            };
            this.bulkRunning = false;
            this.showResultModal = true;
        },
        closeBulkResultModal() {
            this.showResultModal = false;
        }
    }
};

export default retentionBulkRunnerMixin;
